// src/FormulaireSejour.jsx
import { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import PriceCalendarModal from './PriceCalendarModal';
import ChildrenDOBModal from './ChildrenDOBModal';
import { useHistory, useLocation } from 'react-router-dom';
import { readFormDraft, writeFormDraft, clearFormDraft } from 'b2b/utils/formDraftStore';
import { getBookingsBaseUrl, getAuthHeaders, getB2BToken } from '../utils/bookingsBase';

/* ---------------- helpers ---------------- */

const pickCover = (p = {}) =>
  p?.media?.[0]?.url ||
  p?.imageUrl ||
  p?.image ||
  p?.mainImage ||
  p?.coverUrl ||
  p?.picture?.url ||
  p?.images?.[0]?.url ||
  p?.photos?.[0]?.url ||
  null;

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');
const norm = (x) =>
  String(x ?? '')
    .toUpperCase()
    .replace(/^_?LT_/, '');
// --- Dates en LOCAL (pas UTC) ---
// formate une Date en YYYY-MM-DD sans passer par toISOString()
const toISODateLocal = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
// normalise une valeur (Date|string) vers YYYY-MM-DD, en local
const normalizeYMD = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return toISODateLocal(new Date(s));
};
// ancienne signature conservée
const toInputDate = normalizeYMD;
const addDays = (d, n) => {
  const nd = d instanceof Date ? new Date(d) : new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};

const toInt = (v) => Number.parseInt(v, 10);

const isValidDate = (y, m, d) => {
  if (!y || !m || !d) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const sanitizeDate = (raw) => normalizeYMD(raw);

const todayISO = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toISODateLocal(d);
})();

// essaie plusieurs sources pour trouver une date par défaut "proposée"
const findProposedDate = (produit = {}, initial = {}) => {
  const pick = (...vs) => vs.find(Boolean);
  const raw = pick(
    initial.date,
    produit?.bestPrice?.date,
    produit?.bestPrice?.Begin,
    produit?.bestPrice?.bestPrice?._$Begin,
    produit?.defaultDate,
    produit?.departureDate,
    Array.isArray(produit?.availableDates) && produit.availableDates[0],
  );
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(0, 0, 0, 0);
  return toISODateLocal(d); // "YYYY-MM-DD" (local)
};

// calcule l'âge à une date de référence
const ageOn = (dob, ref) => {
  if (!(dob instanceof Date) || Number.isNaN(dob.getTime())) return NaN;
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
  return age;
};

export function validateDOBs(expectedCount, childrenDOB = [], endDateISO) {
  const errors = [];
  const missingIdx = [];
  const invalidIdx = [];
  const tooOldIdx = [];

  for (let i = 0; i < expectedCount; i++) {
    const item = childrenDOB[i];
    const d = toInt(item?.day);
    const m = toInt(item?.month);
    const y = toInt(item?.year);

    if (!d || !m || !y) {
      missingIdx.push(i);
      continue;
    }
    if (!isValidDate(y, m, d)) {
      invalidIdx.push(i);
      continue;
    }
    if (endDateISO) {
      const age = ageOn(y, m, d, endDateISO);
      if (age >= 12) {
        tooOldIdx.push(i);
      }
    }
  }

  if (missingIdx.length) {
    errors.push(
      `Date de naissance requise pour l’enfant${
        missingIdx.length > 1 ? 's' : ''
      } ${missingIdx.map((i) => i + 1).join(', ')}`,
    );
  }
  if (invalidIdx.length) {
    errors.push(
      `Date invalide pour l’enfant${invalidIdx.length > 1 ? 's' : ''} ${invalidIdx
        .map((i) => i + 1)
        .join(', ')}`,
    );
  }
  if (tooOldIdx.length) {
    errors.push(
      `Ces enfant(s) auront 12 ans avant la fin du séjour : ${tooOldIdx
        .map((i) => i + 1)
        .join(', ')}`,
    );
  }

  return { ok: errors.length === 0, errors, missingIdx, invalidIdx, tooOldIdx };
}

const parseDOB = ({ year, month, day }) =>
  new Date(Number(year), Number(month) - 1, Number(day));

/** Retourne le nombre de bébés / enfants / “devenus adultes” à la FIN du séjour. */
function classifyChildrenAtEnd(childrenDOB = [], endISO) {
  const end = endISO ? new Date(endISO) : null;
  let infants = 0,
    kids = 0,
    becomeAdults = 0;

  const enriched = (childrenDOB || []).map((c) => {
    const dob = parseDOB(c || {});
    const ageEnd = end && !Number.isNaN(dob.getTime()) ? ageOn(dob, end) : null;
    let isInfant = false,
      isChild = false;

    if (ageEnd != null) {
      if (ageEnd < 0) {
        // futur / invalide -> on laissera la validation du modal gérer
      } else if (ageEnd < 2) {
        infants++;
        isInfant = true;
      } else if (ageEnd < 12) {
        kids++;
        isChild = true;
      } else {
        becomeAdults++;
      }
    }
    return { ...c, ageOnEnd: ageEnd, isInfant, isChild };
  });

  return { infants, children: kids, becomeAdults, enriched };
}
/** Libellé d'affichage: remplace "Ter" par "Vol non inclus" */
const displayFromTitle = (f) =>
  norm(f?.title) === 'TER' ? 'Vol non inclus' : capFirst(f?.title);

/* --------------- composant --------------- */
function FormulaireSejour({
  produit,
  onSubmit,
  initialValues,
  bookingId: bookingIdProp,
  priceHuman,
}) {
  const history = useHistory();
  const { state } = useLocation();
  const navFormData = state?.formData || null; // 👈 ce qu’on renvoie depuis StepNav
  const initialSnapshotRef = useRef(null); // 👈 AJOUT

  /* ---- Initial / IDs produit d'abord ---- */
  const initial = initialValues || {};
  const productCode = produit?._$id || produit?.id || ''; // code PGI
  const productDbId = String(produit?._id || produit?.id || '').trim(); // _id Mongo
  const productId = /^[a-f0-9]{24}$/i.test(productDbId) ? productDbId : undefined;
  const proposedDate = useMemo(() => findProposedDate(produit, initial), [produit, initial]);

  /* ---- Draft + flags session ---- */
  const draft = readFormDraft(productDbId) || {};
  const draftExists = !!(draft && Object.keys(draft).length);
  const hasSession = (() => {
    try {
      const raw = sessionStorage.getItem(`bookingCtx:${productDbId}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const rawInitial = initialValues || {};
  const initialFormData = rawInitial.formData || {};

  const mergedInitial = useMemo(
    () => ({
      ...rawInitial, // bookingId, productId, search, etc.
      ...initialFormData, // adults, children, childrenDOB, date...
      ...draft, // brouillon éventuel
      ...(hasSession || {}), // sessionStorage si tu l’utilises
      ...(navFormData || {}), // formData venant de StepNav
    }),
    [rawInitial, initialFormData, draft, hasSession, navFormData],
  );

  const baseInit = mergedInitial?.formData || mergedInitial || {};

  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(bookingIdProp || initial?.bookingId || null);

  /* ---- Froms + bestFrom par défaut ---- */
  const froms = produit?.froms ?? produit?.Froms ?? [];
  const bestFromCode =
    produit?.bestPrice?.bestPrice?._$From ?? produit?.bestPrice?._$From ?? '';

  // Fallback : si l’API ne renvoie aucun départ, on créée une option à partir du retour
  const fromsForUI = useMemo(() => {
    if (Array.isArray(froms) && froms.length) return froms;
    if (initial.fromLabel || initial.from) {
      return [
        {
          id: initial.fromCode || initial.from || 'TER',
          title: initial.fromLabel || initial.from || 'Vol non inclus',
        },
      ];
    }
    return [];
  }, [froms, initial.fromCode, initial.fromLabel, initial.from]);

  const matchedFrom = useMemo(
    () =>
      fromsForUI.find(
        (f) => norm(f?.id) === norm(bestFromCode) || norm(f?.title) === norm(bestFromCode),
      ),
    [fromsForUI, bestFromCode],
  );

  // priorité au code pour retrouver le bon libellé
  const byCode = useMemo(
    () => fromsForUI.find((f) => norm(f?.id) === norm(initial?.fromCode)),
    [fromsForUI, initial?.fromCode],
  );

  const initialFromTitle =
    byCode?.title ||
    initial.fromLabel ||
    initial.from ||
    matchedFrom?.title ||
    fromsForUI?.[0]?.title ||
    '';

  const [fromTitle, setFromTitle] = useState(initialFromTitle);

  // code utilisé côté API
  const fromCode = useMemo(() => {
    const byTitle = fromsForUI.find((f) => f.title === fromTitle);
    if (byTitle?.id) return byTitle.id;
    if (initial.fromCode) return initial.fromCode;
    return matchedFrom?.id || fromsForUI?.[0]?.id || '';
  }, [fromsForUI, fromTitle, matchedFrom?.id, initial.fromCode]);

  useEffect(() => setFromTitle(initialFromTitle), [initialFromTitle]);

  /* Libellé courant pour affichage/export */
  const currentFrom = useMemo(() => fromsForUI.find((f) => f.title === fromTitle) || null, [
    fromsForUI,
    fromTitle,
  ]);
  const departCityLabel = displayFromTitle(currentFrom || { title: fromTitle });

  const normIATA = (s) =>
    String(s || '')
      .toUpperCase()
      .replace(/^_?LT_/, '')
      .slice(0, 3);

  // TER / Vol non inclus ?
  const isNoFlight = (fromTitle, fromCode) => {
    const t = String(fromTitle || '')
      .toUpperCase()
      .trim();
    const c = String(fromCode || '')
      .toUpperCase()
      .replace(/^_?LT_/, '')
      .trim();
    return t === 'VOL NON INCLUS' || c === 'TER';
  };

  const { noFlight, originForAPI } = useMemo(() => {
    // l’objet correspondant à l’option choisie (tu l’as déjà en `currentFrom`,
    // mais on le recalcule ici pour être autonome)
    const byTitle = fromsForUI.find((f) => String(f.title) === String(fromTitle)) || null;

    // id réel PGI de cette option (ex: "606" ou "_LT_606" ou "TER")
    const id = byTitle?.id || fromCode || '';

    // détection "Vol non inclus" robuste
    const no =
      norm(byTitle?.id) === 'TER' ||
      norm(byTitle?.title) === 'TER' ||
      /VOL\s*NON\s*INCLUS/i.test(String(byTitle?.title || '')) ||
      norm(fromTitle) === 'TER';

    return { noFlight: no, originForAPI: no ? 'XXX' : id };
  }, [fromsForUI, fromTitle, fromCode]);

  const buildDurationsFromInfo = (produit = {}) => {
    const info = produit?.information;
    if (!info) return [];

    const rawCond = info.ConditionDeparture || info.conditionDeparture || '';
    if (!rawCond) return [];

    const [minN, maxN] = String(rawCond)
      .split('-')
      .map((s) => Number(s.trim()));

    if (!Number.isFinite(minN) || !Number.isFinite(maxN) || maxN < minN) {
      return [];
    }

    // Décalage (jours = nuits + offset) -> ex: 3j / 2n => offset = 1
    const offset =
      Number.isFinite(Number(info.Day)) && Number.isFinite(Number(info.Night))
        ? Number(info.Day) - Number(info.Night)
        : 1;

    const list = [];
    for (let n = minN; n <= maxN; n += 1) {
      list.push({ nights: n, days: n + offset });
    }
    return list;
  };

  const normalizeDurations = (list = []) => {
    const seen = new Set();

    return (Array.isArray(list) ? list : [])
      .map((d) => ({
        days: Number(d.days ?? d.Day ?? d.day),
        nights: Number(d.nights ?? d.Night ?? d.night),
      }))
      .filter((d) => Number.isFinite(d.days) && Number.isFinite(d.nights))
      .filter((d) => d.days > 0 && d.nights > 0)
      .filter((d) => {
        const key = `${d.days}-${d.nights}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.nights - b.nights);
  };

  const cleanDurations = (list = []) => {
    const map = new Map();

    for (const item of Array.isArray(list) ? list : []) {
      const nights = Number(item?.nights ?? item?.Night ?? item?.night);
      const days = Number(item?.days ?? item?.Day ?? item?.day);

      if (!Number.isFinite(days) || !Number.isFinite(nights)) continue;
      if (days <= 0 || nights <= 0) continue;

      // Une seule durée par nombre de nuits
      if (!map.has(nights)) {
        map.set(nights, { days, nights });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.nights - b.nights);
  };

  const filterByInfoDurations = (apiList = [], infoList = []) => {
    const api = cleanDurations(apiList);
    const info = cleanDurations(infoList);

    // Si ConditionDeparture existe, elle est la source officielle
    if (!info.length) return api;

    const allowedNights = new Set(info.map((d) => d.nights));

    const filtered = api
      .filter((d) => allowedNights.has(d.nights))
      .map((d) => info.find((x) => x.nights === d.nights) || d);

    // Si l'API vide tout, on garde les durées produit
    return filtered.length ? cleanDurations(filtered) : info;
  };

  const pickDuration = (list = [], wantedNights) => {
    const clean = cleanDurations(list);
    if (!clean.length) return null;

    const nights = Number(wantedNights);
    return clean.find((d) => d.nights === nights) || clean[0];
  };

  const infoDurations = useMemo(() => buildDurationsFromInfo(produit), [produit]);

  // 2) Fallback si jamais l'info PGI est absente / incohérente
  const fallbackDurations = useMemo(
    () =>
      Array.from({ length: 19 }, (_, i) => {
        const days = i + 4;
        return { days, nights: days - 1 };
      }),
    [],
  );

  // 3) Liste de base utilisée avant réponse de l'API /available-durations
  const baseDurations = infoDurations.length ? infoDurations : fallbackDurations;

  const [durations, setDurations] = useState(baseDurations);
  const [loadingDur, setLoadingDur] = useState(false);

  const findIdx = (arr, nights) =>
    Math.max(
      0,
      arr.findIndex((d) => d.nights === nights),
    );

  const defaultNights = Number(produit?.information?.Night ?? baseDurations[0]?.nights ?? 3);

  const initialDur = initial.nights
    ? {
        nights: Number(initial.nights),
        days: Number(initial.days || Number(initial.nights) + 1),
      }
    : null;

  const [selectedDuree, setSelectedDuree] = useState(() => {
    if (initialDur) return initialDur;
    const idx = findIdx(baseDurations, defaultNights || baseDurations[0]?.nights);
    return baseDurations[idx] || baseDurations[0];
  });

  useEffect(() => {
    let mounted = true;
    if (!productCode || !fromCode) return;

    setLoadingDur(true);

    axios
      .get(`/api/prices/available-durations`, {
        params: { code: productCode, from: originForAPI },
        timeout: 15000,
      })
      .then((res) => {
        if (!mounted) return;

        const avail = Array.isArray(res.data?.durations) ? res.data.durations : [];

        const finalDurations =
          avail.length > 0
            ? filterByInfoDurations(avail, infoDurations)
            : cleanDurations(baseDurations);

        const safeDurations = finalDurations.length
          ? finalDurations
          : cleanDurations(baseDurations);

        setDurations(safeDurations);

        setSelectedDuree((prev) => {
          const wantedNights = initial.nights ?? prev?.nights ?? defaultNights;
          return pickDuration(safeDurations, wantedNights);
        });
      })
      .catch(() => {
        if (!mounted) return;

        const safeDurations = cleanDurations(baseDurations);

        setDurations(safeDurations);

        setSelectedDuree((prev) => {
          const wantedNights = initial.nights ?? prev?.nights ?? defaultNights;
          return pickDuration(safeDurations, wantedNights);
        });
      })
      .finally(() => mounted && setLoadingDur(false));

    return () => {
      mounted = false;
    };
  }, [
    productCode,
    fromCode,
    originForAPI,
    baseDurations,
    infoDurations,
    defaultNights,
    initial.nights,
  ]);

  useEffect(() => {
    // on ne crée le snapshot qu'une seule fois
    if (initialSnapshotRef.current) return;

    initialSnapshotRef.current = {
      fromTitle,
      selectedDuree,
      date,
      adults,
      children,
      childrenDOB,
    };
  }, [fromTitle, selectedDuree, date, adults, children, childrenDOB]);

  // Toujours afficher la date proposée quand elle est valide
  // useEffect(() => {
  //   const prop = toInputDate(proposedDate);
  //   if (!prop) return;
  //   // seulement si on n'a pas déjà une date "saine" OU si la date actuelle diffère
  //   if (!date || date !== prop) {
  //     // ne pas afficher une date passée vs système
  //     if (prop >= todayMidnightISO) {
  //       setDate(prop);
  //       setDisplayedPrice(null); // on laissera le calendrier remettre un prix si besoin
  //     }
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [proposedDate, fromCode, selectedDuree?.nights]);

  /* ---- Date & voyageurs ---- */
  const [date, setDate] = useState(() => {
    const init = sanitizeDate(mergedInitial.date);
    const prop = sanitizeDate(proposedDate);
    const today = todayISO;
    const floor = prop ? (prop < today ? today : prop) : today; // plancher = max(proposed, today)
    if (!init || init < floor) return floor;
    return init;
  });

  const [displayedPrice, setDisplayedPrice] = useState(
    initial.displayedPriceCents != null
      ? Number(initial.displayedPriceCents) / 100
      : Number.isFinite(initial.pricePerPerson)
      ? Number(initial.pricePerPerson)
      : null,
  );
  const [adults, setAdults] = useState(
    Number.isFinite(baseInit.adults) ? Number(baseInit.adults) : 2,
  );
  const [children, setChildren] = useState(
    Number.isFinite(baseInit.children) ? Number(baseInit.children) : 0,
  );

  const [openCal, setOpenCal] = useState(false);

  // enfants: dates de naissance
  const [childrenDOB, setChildrenDOB] = useState(
    Array.isArray(baseInit.childrenDOB) ? baseInit.childrenDOB : [],
  );
  const [openDOB, setOpenDOB] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // Pour signaler à la modale quels index sont invalides (optionnel)
  const [dobInvalidIdx, setDobInvalidIdx] = useState([]);

  const invalid = (k) => submitted && !!errors[k];
  // Ne jamais effacer une date déjà choisie pendant l’hydratation
  useEffect(() => {
    if (date) return;
    setDate('');
    setDisplayedPrice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDuree, fromCode]);

  // si le nombre d'enfants change, on nettoie la saisie précédente
  useEffect(() => {
    setChildrenDOB((prev) => prev.slice(0, Number(children) || 0));
  }, [children]);

  useEffect(() => {
    const prop = sanitizeDate(proposedDate);
    if (!prop) return;
    const floor = prop < todayISO ? todayISO : prop;
    if (!date || date < floor) {
      setDate(floor);
      setDisplayedPrice(null);
    }
    // on dépend de la vraie origine envoyée à l’API
  }, [proposedDate, originForAPI, selectedDuree?.nights]);

  const endDate = useMemo(() => {
    if (!date || !selectedDuree?.nights) return '';
    return toISODateLocal(addDays(date, selectedDuree.nights));
  }, [date, selectedDuree]);

  const fmtFR = (d) => {
    try {
      return new Date(d).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return d;
    }
  };

  function handlePick({ dateISO, price }) {
    setDate(toInputDate(dateISO));
    setDisplayedPrice(Number(price));
    setOpenCal(false);
  }

  /* ---- classification fin de séjour ---- */
  const classes = useMemo(() => classifyChildrenAtEnd(childrenDOB, endDate), [
    childrenDOB,
    endDate,
  ]);
  const effectiveAdults = Number(adults || 0) + (classes?.becomeAdults || 0);
  const effectiveChildren = classes?.children || 0;
  const effectiveInfants = classes?.infants || 0;

  const paxPayant = effectiveAdults + effectiveChildren;
  const totalPreview =
    displayedPrice != null ? Number((Number(displayedPrice) * paxPayant).toFixed(2)) : null;

  /* ---------------- valeurs proposées (baseline) ---------------- */
  const baselineRef = useRef(null);

  // 1) snapshot initial (avant retour d'API durées)
  useEffect(() => {
    if (baselineRef.current) return;
    baselineRef.current = {
      fromTitle: initialFromTitle,
      selectedDuree:
        initialDur ||
        fallbackDurations[findIdx(fallbackDurations, defaultNights)] ||
        fallbackDurations[0],
      date: proposedDate || '', // ex: "2025-11-30"
      adults: Number.isFinite(initial.adults) ? Number(initial.adults) : 1,
      children: Number.isFinite(initial.children) ? Number(initial.children) : 0,
      childrenDOB: Array.isArray(initial.childrenDOB) ? initial.childrenDOB : [],
    };
  }, [initialFromTitle, initialDur, fallbackDurations, defaultNights, initial, proposedDate]);

  // 2) quand les durées “réelles” arrivent, forcer la nuitée proposée (ex: 5)
  useEffect(() => {
    if (!durations?.length || !baselineRef.current) return;
    const wantedNights = Number(initial.nights ?? defaultNights);
    const idx = findIdx(durations, wantedNights);
    baselineRef.current.selectedDuree = durations[idx] || durations[0];
  }, [durations, initial.nights, defaultNights]);

  /* ---------------------- Upsert booking (POST/PATCH) ---------------------- */

  async function upsertBooking(payload) {
    const token = getB2BToken();
    const headers = getAuthHeaders();

    // Base dépend du token
    const base = getBookingsBaseUrl();

    if (bookingId) {
      // ⚠️ Option: en B2C, je te conseille d’éviter PATCH public au début
      // Donc si pas de token, tu peux forcer un POST (nouvelle résa) ou gérer un public PATCH plus tard.
      const { data } = await axios.patch(`${base}/${bookingId}`, payload, {
        headers,
        timeout: 15000,
      });
      return data;
    } else {
      const { data } = await axios.post(base, payload, { headers, timeout: 15000 });
      setBookingId(data?._id || data?.id || null);
      return data;
    }
  }

  /* -------------------------------- Submit -------------------------------- */
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setSubmitted(true);

    const displayedPriceCents =
      displayedPrice != null ? Math.round(Number(displayedPrice) * 100) : null;

    // --- validation DOB enfants (inchangé) ---
    if (mustFillDOB) {
      const expected = Number(children) || 0;
      const { ok, errors: dobErrs, missingIdx, invalidIdx, tooOldIdx } = validateDOBs(
        expected,
        childrenDOB,
        endDate,
      );

      if (!ok) {
        setErrors((prev) => ({
          ...prev,
          childrenDOB: dobErrs.join(' '),
        }));
        setDobInvalidIdx([...new Set([...missingIdx, ...invalidIdx, ...tooOldIdx])]);
        setOpenDOB(true);
        setTimeout(() => {
          const first = document.querySelector(
            `[data-dob-index="${missingIdx[0] ?? invalidIdx[0] ?? tooOldIdx[0] ?? 0}"]`,
          );
          first?.focus?.();
        }, 0);
        return;
      } else {
        setErrors((p) => ({ ...p, childrenDOB: undefined }));
        setDobInvalidIdx([]);
      }
    }

    // payload juste pour le contexte, PAS pour la DB
    const payload = {
      nights: selectedDuree?.nights,
      days: selectedDuree?.days,
      from: fromTitle,
      fromLabel: departCityLabel,
      fromCode,
      originForAPI,
      noFlight,
      date,
      endDate,
      adults: Number(adults),
      children: Number(children),
      infantsCount: effectiveInfants,
      childrenClassified: {
        infants: effectiveInfants,
        children: effectiveChildren,
        becomeAdults: classes.becomeAdults,
      },
      childrenDOB: classes.enriched,
      displayedPriceCents,
    };

    try {
      if (mountedRef.current) setLoading(true);

      if (onSubmit) {
        try {
          await onSubmit(payload);
        } catch (_) {}
      }

      const destinationCode = normIATA(produit?.to?.id);
      const imageUrl = pickCover(produit);

      const nextState = {
        bookingId: null, // 👉 on ne crée plus encore
        productId,
        product: {
          hotelName: produit?.description?.name,
          city: produit?.location?.city?.title || '',
          country: produit?.location?.country?.title || '',
          cat: produit?.productType,
          _id: productId,
          imageUrl,
        },
        search: {
          origin: originForAPI,
          destination: String(destinationCode)
            .toUpperCase()
            .replace(/^_?LT_/, ''),
          startDate: date,
          endDate,
          departCity: departCityLabel,
          noFlight,
          adults: Number(adults) + (classes?.becomeAdults || 0),
          children: effectiveChildren,
          infants: effectiveInfants,
          productCode,
        },
        nights: selectedDuree?.nights || 1,
        priceSeed: {
          stay: displayedPrice != null ? Number(displayedPrice) : 0,
          fees: 0,
          taxes: 0,
          currency: 'EUR',
        },
        formData: payload,
      };

      try {
        if (productId) {
          sessionStorage.setItem(`bookingCtx:${productId}`, JSON.stringify(nextState));
        }
      } catch {}
      const token = localStorage.getItem('token_b2b');
      const isB2B = !!token;
      history.push(isB2B ? '/b2b/selection' : '/selection', nextState);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || err?.message || 'Erreur de réservation');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  /** Réinitialise -> revient aux valeurs proposées du produit */
  function handleReset() {
    // just pour vérifier que ça passe bien ici
    try {
      sessionStorage.clear();
    } catch {}
    try {
      if (productDbId) clearFormDraft(productDbId);
    } catch {}

    const s = initialSnapshotRef.current;
    if (!s) return;

    setFromTitle(s.fromTitle);
    setDurations((prev) => (prev?.length ? prev : fallbackDurations));
    setSelectedDuree(s.selectedDuree);
    setDate(s.date);
    setDisplayedPrice(null);
    setAdults(2);
    setChildren(0);
    setChildrenDOB([]);
    setOpenCal(false);
    setOpenDOB(false);
  }

  const durationsForSelect = useMemo(() => {
    const clean = cleanDurations(durations);
    return clean.length ? clean : cleanDurations(baseDurations);
  }, [durations, baseDurations]);

  const selectedDurationValue =
    selectedDuree?.days && selectedDuree?.nights
      ? `${selectedDuree.days}-${selectedDuree.nights}`
      : durationsForSelect[0]
      ? `${durationsForSelect[0].days}-${durationsForSelect[0].nights}`
      : '';

  const mustFillDOB = Number(children || 0) > 0;
  const dobToLabel = (c) =>
    c && c.day && c.month && c.year
      ? `${String(c.day).padStart(2, '0')}/${String(c.month).padStart(2, '0')}/${c.year}`
      : '—';

  // Afficher le bouton Réinitialiser seulement si utile
  const canReset = useMemo(() => {
    return Boolean(
      date ||
        displayedPrice != null ||
        Number(adults) !== 1 ||
        Number(children) > 0 ||
        (childrenDOB && childrenDOB.length > 0) ||
        (fromTitle && fromTitle !== '') ||
        draftExists ||
        hasSession,
    );
  }, [
    date,
    displayedPrice,
    adults,
    children,
    childrenDOB?.length,
    fromTitle,
    draftExists,
    hasSession,
  ]);

  /* ---- Calendrier : clé de remount + minDate aujourd’hui ---- */
  const calendarKey = useMemo(
    () => `${productCode}|${originForAPI}|${selectedDuree?.nights || 0}`,
    [productCode, originForAPI, selectedDuree?.nights],
  );
  const todayMidnightISO = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return toISODateLocal(d); // "YYYY-MM-DD" (local)
  }, []);

  const fromsForUIFormatted = fromsForUI
    // ❌ supprimer Paris CDG & Paris Orly
    .filter((f) => !['paris cdg', 'paris orly'].includes(f.title))

    // ⭐ mettre Paris en premier
    .sort((a, b) => {
      if (a.title === 'paris') return -1;
      if (b.title === 'paris') return 1;
      return a.title.localeCompare(b.title);
    });
  const shown = sanitizeDate(date || proposedDate);
  /* --------------------------------- UI --------------------------------- */
  return (
    <form
      onSubmit={handleSubmit}
      className="p-3 border bg-light"
      style={{ borderRadius: '4px' }}
    >
      <h2 className="mb-3 text-center" style={{ color: '#f56371', fontWeight: '300' }}>
        Réserver ce séjour
      </h2>
      <hr style={{ borderTop: '1px solid #00006440' }} />
      {/* Ville de départ */}
      <div className="mb-3">
        <label className="form-label">Ville de départ</label>
        <select
          className="form-control"
          value={fromTitle}
          onChange={(e) => setFromTitle(e.target.value)}
          style={{ fontSize: '16px', color: '#000064' }}
        >
          {fromsForUIFormatted.map((f, i) => (
            <option key={i} value={f.title} style={{ fontSize: '16px', color: '#000064' }}>
              {displayFromTitle(f)}
            </option>
          ))}
        </select>
      </div>

      {/* Durée */}
      <div className="mb-3">
        <label className="form-label">
          Durée {loadingDur && <small className="text-muted"> (chargement…)</small>}
        </label>
        <select
          className="form-control"
          value={selectedDurationValue}
          onChange={(e) => {
            const [d, n] = e.target.value.split('-').map(Number);
            setSelectedDuree({ days: d, nights: n });
          }}
          style={{ fontSize: '16px', color: '#000064' }}
        >
          {durationsForSelect.map((d) => (
            <option
              key={`${d.days}-${d.nights}`}
              value={`${d.days}-${d.nights}`}
              style={{ fontSize: '16px', color: '#000064' }}
            >
              {d.days} jours {d.nights} nuits
            </option>
          ))}
        </select>
        {!!date && !!endDate && (
          <small className="text-muted">
            Séjour du <strong>{fmtFR(date)}</strong> au <strong>{fmtFR(endDate)}</strong> (
            {selectedDuree.nights} nuit{selectedDuree.nights > 1 ? 's' : ''})
          </small>
        )}
      </div>

      {/* Date de départ */}
      <div className="mb-3">
        <label className="form-label">Date de départ</label>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setOpenCal(true)}
            style={{ width: '100%' }}
          >
            {fmtFR(shown) || 'Choisir une date'}
          </button>
        </div>
        {(() => {
          if (!shown) return null;
          const shownEnd =
            shown && selectedDuree?.nights
              ? fmtFR(addDays(shown, selectedDuree.nights))
              : endDate
              ? fmtFR(endDate)
              : null;
          return (
            <small className="text-muted d-block mt-2">
              Départ le <strong>{fmtFR(shown)}</strong>
              {shownEnd && (
                <>
                  {' '}
                  • Retour le <strong>{shownEnd}</strong>
                </>
              )}
            </small>
          );
        })()}
      </div>

      {/* Voyageurs */}
      <div className="row g-2 mb-3">
        <div className="col">
          <label className="form-label">Adultes</label>
          <input
            type="number"
            min={1}
            className="form-control"
            value={adults}
            onChange={(e) => setAdults(Number(e.target.value))}
          />
        </div>
        <div className="col">
          <label className="form-label">Enfants</label>
          <input
            type="number"
            min={0}
            className="form-control"
            value={children}
            onChange={(e) => setChildren(Number(e.target.value))}
          />
        </div>
      </div>

      {/* DOB + breakdown */}
      {mustFillDOB && (
        <div className="mb-3" data-error-key="childrenDOB">
          <button
            type="button"
            className="btn btn-outline-secondary w-100"
            onClick={() => setOpenDOB(true)}
            disabled={!date}
            title={!date ? 'Choisissez d’abord une date de départ' : ''}
            aria-invalid={invalid('childrenDOB')}
          >
            {childrenDOB.length === Number(children)
              ? `Modifier la date de naissance des ${children} enfant(s)`
              : `Renseigner la date de naissance des ${children} enfant(s)`}
          </button>
          {invalid('childrenDOB') && (
            <div className="invalid-feedback d-block">{errors.childrenDOB}</div>
          )}

          {childrenDOB.length > 0 && (
            <div className="alert alert-light border mt-2 mb-0 small">
              <div className="fw-semibold mb-1">Dates de naissance</div>
              {childrenDOB.map((c, i) => (
                <div key={i}>
                  Enfant {i + 1}: <strong>{dobToLabel(c)}</strong>
                  {date && typeof c.ageOnDeparture === 'number' && (
                    <>
                      {' '}
                      —{' '}
                      <span className="text-muted">âge au départ: {c.ageOnDeparture} ans</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="small mt-2">
            <div className="text-muted">
              Bébés (&lt; 2 ans à la fin) : <strong>{effectiveInfants}</strong>
            </div>
            <div className="text-muted">
              Enfants (2–11 ans à la fin) : <strong>{effectiveChildren}</strong>
            </div>
            {!!classes.becomeAdults && (
              <div className="text-danger">
                {classes.becomeAdults} enfant(s) auront 12 ans avant la fin du séjour et seront
                comptés comme <strong>adultes</strong>.
              </div>
            )}
            <div className="text-muted mt-1">
              Calcul basé sur <strong>{paxPayant}</strong> passager(s) payant(s) (hors
              bébé[s]).
            </div>
          </div>
        </div>
      )}

      {/* Aperçu du prix */}
      {/* {displayedPrice != null && (
        <div className="text-center m-3" >
          <h3 style={{ color: '#f56371' }}><strong>{priceHuman}</strong> / Pers</h3> */}
      {/* <strong>{totalPreview?.toFixed(2)} €</strong> Total */}
      {/* </div>
      )} */}

      {(displayedPrice != null || !!priceHuman) && (
        <div className="text-center m-3">
          <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '0.95rem', marginRight: 6, marginTop: 10 }}>Dès</span>

            <span
              style={{ fontSize: '2.2rem', fontWeight: 300, lineHeight: 1, color: '#f56371' }}
            >
              {displayedPrice != null
                ? Math.round(displayedPrice)
                : String(priceHuman || '').trim()}
            </span>

            <span style={{ marginLeft: 6, textAlign: 'left', lineHeight: 1.1 }}>
              <span style={{ fontSize: '0.75rem', display: 'block', marginTop: 2 }}>
                ttc /
              </span>
              <span style={{ fontSize: '0.75rem', display: 'block', marginTop: 2 }}>pers</span>
            </span>
          </div>
        </div>
      )}

      <div className="d-flex gap-2 mt-2">
        <button
          type="submit"
          className="btn btn-primary flex-fill m-1 font-weight-normal "
          disabled={loading || !date}
          style={{ fontSize: '16px' }}
        >
          {loading ? 'Réservation…' : 'Réserver'}
        </button>
        {canReset && (
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-outline-secondary flex-fill m-1 font-weight-normal"
            disabled={loading}
            title="Revenir aux infos proposées par le produit"
            style={{ fontSize: '16px' }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Modals */}
      <PriceCalendarModal
        key={calendarKey}
        isOpen={openCal}
        onClose={() => setOpenCal(false)}
        onSelect={handlePick}
        productCode={productCode}
        defaultFrom={originForAPI}
        nights={selectedDuree?.nights}
        minDate={
          sanitizeDate(proposedDate) && sanitizeDate(proposedDate) > todayMidnightISO
            ? sanitizeDate(proposedDate)
            : todayMidnightISO
        }
      />

      <ChildrenDOBModal
        isOpen={openDOB}
        onClose={() => setOpenDOB(false)}
        count={Number(children) || 0}
        departDate={date}
        endDate={endDate}
        initialDOB={childrenDOB}
        invalidIdx={dobInvalidIdx}
        onApply={({ childrenDOB: list }) => setChildrenDOB(list || [])}
      />
    </form>
  );
}

export default FormulaireSejour;
